import React, {
  useState,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";
import { Box, Typography, TextField } from "@mui/material";
import { themes } from "services/constants";
import { CustomButton } from "components/CustomComponents";
import { CustomAutoComplete, CustomTextBox } from "components/CustomComponents";
import { useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { useFetch } from "services/hooks/useFetch";
import { onlyNumberRegex } from "services/constants/patterns";
import { ShiftAssignmentForShift } from "../../ShiftRepresentation/ShiftAssignmentForShift";
import { ShiftAssignmentForWeekDays } from "../../ShiftRepresentation/ShiftAssignmentForWeekDays";
import {
  weekdays,
  daysDetails,
} from "pages/Administration/AdministrationHeaders";
import dayjs from "dayjs";
import { useShiftRota } from "../../ShiftRotaContext";

export const CloneShiftRotaSchedule = forwardRef(
  ({ handleNext, handleBack, onUpdate }) => {
    const {
      configRef,
      formData,
      WeekDaysData,
      setWeekDaysData,
      allGroupDetails,
      shiftCycleDetails,
      setShiftCycleDetails,
      generateShiftMatrics,
    } = useShiftRota();
    const {
      setValue,
      register,
      watch,
      reset,
      handleSubmit,
      getValues,
      resetField,
      formState: { errors },
    } = useForm({
      mode: "onBlur",
      defaultValues: {
        ...formData?.schedule,
      },
    });

    const { domain } = useSelector((state) => state.tokenReducer);
    const [rotationDates, setRotationDates] = useState([]);
    const [updateData, setUpdateData] = useState(true);
    const [rotationDay, setRotationDay] = useState("");
    const [rotationDate, setRotationDate] = useState("");
    const [firstRotationDateDate, setFirstRotationDate] = useState("");
    const [weekdayError, setWeekDayError] = useState(false);
    const [shiftError, setShiftError] = useState(false);
    const [autoSftAssignData, setAutoSftAssignData] = useState("");
    const [rotationFrequency, setRotationFrequency] = useState("");
    const [rotationBasedOn, setRotationBasedOn] = useState("");

    let effective_from = formData?.config?.effective_from;
    let rotation_frequency = watch("rotation_frequency");
    let duration_for_one_cycle = watch("duration_for_one_cycle");
    let rotation_month_day = watch("rotation_month_day");
    let rotation_day_week = watch("rotation_day_week");

    const getRotationDates = useCallback(() => {
      let dates = [];
      let startDate = dayjs(effective_from);
      if (rotation_frequency === "Month") {
        for (let i = 1; i <= duration_for_one_cycle; i++) {
          let nextDate = startDate?.add(i, "month")?.date(rotation_month_day);
          dates?.push(nextDate?.format("DD/MM/YYYY"));
        }
      } else if (rotation_frequency === "Week") {
        let selectedDayIndex = weekdays.findIndex(
          (day) => day.label === rotation_day_week
        );
        let nextMonday = startDate.day(
          selectedDayIndex >= startDate.day()
            ? selectedDayIndex + 1
            : selectedDayIndex + 8
        );

        for (let i = 0; i < duration_for_one_cycle; i++) {
          dates.push(nextMonday.add(i * 7, "day").format("DD/MM/YYYY"));
        }
      }
      return dates;
    }, [
      duration_for_one_cycle,
      effective_from,
      rotation_day_week,
      rotation_frequency,
      rotation_month_day,
    ]);
    const rotation = [
      {
        label: "Month",
      },
      {
        label: "Week",
      },
    ];

    const AutoSftData = [
      {
        label: "Yes",
      },
      {
        label: "No",
      },
    ];
    const { result: sftFieldData } = useFetch(
      `${domain}administration/shiftroster/shiftgroups/shift-choices/`
    );

    useEffect(() => {
      if (updateData && formData?.schedule) {
        if (!formData?.schedule?.already_created) {
          reset({
            ...formData?.schedule,
            rotation_day_week: "",
            rotation_month_day: "",
            duration_for_one_cycle: "",
            first_rotation_date: "",
          });
        } else {
          reset(formData?.schedule);
          setRotationDate(formData?.schedule?.rotation_month_day);
          setRotationDay(formData?.schedule?.rotation_day_week);
          setFirstRotationDate(formData?.schedule?.first_rotation_date);
        }
        setAutoSftAssignData(formData?.schedule?.auto_sft_assign);
        setRotationFrequency(formData?.schedule?.rotation_frequency);
        setRotationBasedOn(formData?.schedule?.rotation_based_on);
      }
    }, [
      formData?.schedule,
      getValues,
      reset,
      resetField,
      setValue,
      updateData,
      watch,
    ]);

    const updateRotationFrequency = (event, data) => {
      setValue("rotation_frequency", data?.label || "");
      setRotationFrequency(data?.label);
      setValue("rotation_day_week", "");
      setValue("rotation_month_day", "");
      setValue("rotation_month_day", "");
      setValue("duration_for_one_cycle", "");
      setFirstRotationDate("");
      setValue("first_rotation_date", "");
      setRotationDates([]);
      setRotationDay("");
      setRotationDate("");
    };

    const selectWeekDay = (event, data) => {
      setValue("rotation_day_week", data?.label || "");
      setRotationDay(data?.label);
      setFirstRotationDate("");
      setValue("first_rotation_date", "");
    };

    const selectMonthDay = (event, data) => {
      setValue("rotation_month_day", data?.label || "");
      setRotationDate(data?.label);
      setFirstRotationDate("");
      setValue("first_rotation_date", "");
    };

    const updateShiftAssignmentDuration = (event) => {
      if (
        onlyNumberRegex.test(event.target.value) &&
        event.target.value.length <= 3
      ) {
        setValue("duration_for_one_cycle", event.target.value);
        setFirstRotationDate("");
        setValue("first_rotation_date", "");
      }
    };

    useEffect(() => {
      if (
        formData?.config?.effective_from &&
        rotation_frequency &&
        duration_for_one_cycle &&
        (rotation_day_week || rotation_month_day)
      ) {
        const dates = getRotationDates();
        setRotationDates(dates?.map((value) => ({ label: value })));
      }
    }, [
      duration_for_one_cycle,
      effective_from,
      formData?.config?.effective_from,
      getRotationDates,
      rotation_day_week,
      rotation_frequency,
      rotation_month_day,
    ]);

    const handleFirstRotationDate = (event, data) => {
      if (data) {
        setValue("first_rotation_date", data?.label);
        setFirstRotationDate(data?.label);
      }
    };

    const updateAutoSftAssign = (event, data) => {
      if (data) {
        setValue("auto_sft_assign", data?.label);
        setAutoSftAssignData(data?.label);
      }
    };

    const validateWeekDaysShifts = () => {
      let unselectedShifts = WeekDaysData.filter((data) =>
        data.cycles.some((cycData) =>
          cycData.assignments.some((assignment) => assignment.shift_id === "")
        )
      );
      if (unselectedShifts.length === 0) {
        setWeekDayError(false);
        return true;
      } else {
        setWeekDayError(true);
        return false;
      }
    };

    const validateShifts = () => {
      let unselectedShifts = shiftCycleDetails.filter((data) =>
        data.assignments.some((assignment) => assignment.shift_id === "")
      );
      if (unselectedShifts.length === 0) {
        setShiftError(false);
        return true;
      } else {
        setShiftError(true);
        return false;
      }
    };

    useImperativeHandle(configRef, () => ({
      saveFormValues: () => {
        const currentValues = getValues();
        onUpdate({
          ...currentValues,
        });
      },
    }));

    const onSubmit = async (formValues) => {
      const isValid =
        watch("rotation_based_on") === "Weekdays"
          ? validateWeekDaysShifts()
          : validateShifts();
      if (!isValid) {
        return;
      }
      onUpdate({
        ...formValues,
        already_created: true,
        callRepresentationApi: true,
      });
      handleNext(2);
    };

    const updateRotationBasedOn = (event, data) => {
      if (data) {
        setValue("rotation_based_on", data?.label);
        setRotationBasedOn(data?.label);
        generateShiftMatrics(
          formData?.config?.shiftGroup,
          formData?.config?.shift
        );
        if (data?.label === "Weekdays") {
          setRotationFrequency("Week");
          if (getValues("rotation_based_on") === "Weekdays") {
            setValue("rotation_frequency", "Week");
            setValue("rotation_frequency", "Week");
          }
        } else {
          setRotationFrequency("");
        }
      } else {
        setRotationFrequency("");
      }
    };

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{ flexGrow: 1, overflow: "auto", p: 2 }}
          component="form"
          onSubmit={handleSubmit(onSubmit)}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2.5,
            }}
          >
            <Box
              sx={{
                ml: 1,
              }}
            >
              <Typography sx={{ fontWeight: 600 }}>Shift</Typography>
              <Typography variant="body2" color="text.secondary">
                Choose shifts to include in this rota
              </Typography>
            </Box>
            <Box>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 4,
                  ml: 1,
                }}
              >
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Rotation Based On
                    <span style={{ color: themes?.redColor }}>*</span>
                  </Typography>
                  <CustomAutoComplete
                    dataList={
                      sftFieldData?.rotation_based_on_choices ||
                      []?.map((obj) => ({
                        ...obj,
                        label: obj?.label,
                      }))
                    }
                    selectedValue={rotationBasedOn}
                    rules={{
                      ...register("rotation_based_on", {
                        required: "Shift is required",
                      }),
                    }}
                    updateDataFunction={updateRotationBasedOn}
                    placeHolder="Select the rotation frequency"
                    errorMessage={
                      errors?.rotation_based_on?.message
                        ? errors?.rotation_based_on?.message
                        : ""
                    }
                    textBoxError={errors?.rotation_based_on ? true : false}
                  />
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Rotation Frequency
                    <span style={{ color: themes?.redColor }}>*</span>
                  </Typography>
                  {getValues("rotation_based_on") === "Weekdays" ? (
                    <TextField
                      value={rotationFrequency}
                      size="small"
                      fullWidth
                    />
                  ) : (
                    <CustomAutoComplete
                      dataList={rotation?.map((obj) => ({
                        ...obj,
                        label: obj?.label,
                      }))}
                      selectedValue={rotationFrequency}
                      rules={{
                        ...register("rotation_frequency", {
                          required: "Rotation frequency is required",
                        }),
                      }}
                      updateDataFunction={updateRotationFrequency}
                      placeHolder="Select the rotation frequency"
                      errorMessage={
                        errors?.rotation_frequency?.message
                          ? errors?.rotation_frequency?.message
                          : ""
                      }
                      textBoxError={errors?.rotation_frequency ? true : false}
                    />
                  )}
                </Box>
              </Box>
            </Box>
            <Box>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 4,
                  ml: 1,
                }}
              >
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Rotation Day
                    <span style={{ color: themes?.redColor }}>*</span>
                  </Typography>

                  {getValues("rotation_frequency") === "Week" ? (
                    <CustomAutoComplete
                      dataList={weekdays}
                      selectedValue={rotationDay}
                      rules={{
                        ...register("rotation_day_week", {
                          required: false,
                          validate: {
                            minLengthNoSpaces: (value) =>
                              getValues("rotation_frequency") === "Week" &&
                              !value
                                ? "Rotation day is required"
                                : true,
                          },
                        }),
                      }}
                      updateDataFunction={selectWeekDay}
                      placeHolder="Select the rotation day"
                      errorMessage={
                        errors?.rotation_day_week?.message
                          ? errors?.rotation_day_week?.message
                          : ""
                      }
                      textBoxError={errors?.rotation_day_week ? true : false}
                    />
                  ) : (
                    <CustomAutoComplete
                      dataList={daysDetails}
                      selectedValue={rotationDate}
                      rules={{
                        ...register("rotation_month_day", {
                          required: false,
                          validate: {
                            minLengthNoSpaces: (value) =>
                              getValues("rotation_frequency") === "Month" &&
                              !value
                                ? "Rotation month day is required"
                                : true,
                          },
                        }),
                      }}
                      updateDataFunction={selectMonthDay}
                      placeHolder="Select the rotation month day"
                      errorMessage={
                        errors?.rotation_month_day?.message
                          ? errors?.rotation_month_day?.message
                          : ""
                      }
                      textBoxError={errors?.rotation_month_day ? true : false}
                    />
                  )}
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    No of Repetition
                    <span style={{ color: themes?.redColor }}>*</span>
                  </Typography>
                  <CustomTextBox
                    selectedValue={watch("duration_for_one_cycle")}
                    rules={{
                      ...register("duration_for_one_cycle", {
                        required: "Cycle duration is required",
                      }),
                    }}
                    updateDataFunction={updateShiftAssignmentDuration}
                    placeHolder="Enter cycle duration"
                    errorMessage={
                      errors?.duration_for_one_cycle?.message
                        ? errors?.duration_for_one_cycle?.message
                        : ""
                    }
                    textBoxError={errors.duration_for_one_cycle ? true : false}
                  />
                </Box>
              </Box>
            </Box>
            <Box>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 4,
                  ml: 1,
                }}
              >
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    First Rotation Date
                    <span style={{ color: themes?.redColor }}>*</span>
                  </Typography>
                  <CustomAutoComplete
                    dataList={rotationDates}
                    selectedValue={firstRotationDateDate}
                    rules={{
                      ...register("first_rotation_date", {
                        required: "First Rotation Date is required",
                      }),
                    }}
                    updateDataFunction={handleFirstRotationDate}
                    placeHolder="Select the first rotation date"
                    errorMessage={
                      errors?.first_rotation_date?.message
                        ? errors?.first_rotation_date?.message
                        : ""
                    }
                    textBoxError={errors?.first_rotation_date ? true : false}
                  />
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Enable Auto Shift Assign
                    <span style={{ color: themes?.redColor }}>*</span>
                  </Typography>
                  <CustomAutoComplete
                    dataList={AutoSftData?.map((obj) => ({
                      ...obj,
                      label: obj?.label,
                    }))}
                    selectedValue={autoSftAssignData}
                    rules={{
                      ...register("auto_sft_assign", {
                        required: "Auto shift assign is required",
                      }),
                    }}
                    updateDataFunction={updateAutoSftAssign}
                    placeHolder="Select the rotation frequency"
                    errorMessage={
                      errors?.auto_sft_assign?.message
                        ? errors?.auto_sft_assign?.message
                        : ""
                    }
                    textBoxError={errors?.auto_sft_assign ? true : false}
                  />
                </Box>
              </Box>
            </Box>
            <Box
              sx={{
                borderBottom: `1px solid ${themes?.miliedLightGray}`,
                borderRadius: 0,
              }}
            ></Box>
            <Box
              sx={{
                ml: 1,
              }}
            >
              <Typography sx={{ fontWeight: 600 }}>
                Shift Schedule Assignment
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Allocate shifts to specific days of the week for each shift
                group
              </Typography>
            </Box>
            <Box>
              <Box sx={{ ml: 1 }}>
                {rotationBasedOn === "Shift" ? (
                  <ShiftAssignmentForShift
                    shiftError={shiftError}
                    setShiftError={setShiftError}
                    allGroupDetails={allGroupDetails}
                    shiftCycleDetails={shiftCycleDetails}
                    setShiftCycleDetails={setShiftCycleDetails}
                    setUpdateData={setUpdateData}
                  />
                ) : rotationBasedOn === "Weekdays" ? (
                  <ShiftAssignmentForWeekDays
                    WeekDaysData={WeekDaysData}
                    setWeekDaysData={setWeekDaysData}
                    setUpdateData={setUpdateData}
                    weekdayError={weekdayError}
                  />
                ) : null}
              </Box>
            </Box>

            <Box
              sx={{
                ml: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box>
                <CustomButton
                  actionName="Prev"
                  typeName="button"
                  actionFuntion={() => {
                    handleBack(0);
                  }}
                />
              </Box>
              <Box>
                <CustomButton
                  actionName="Next"
                  typeName="submit"
                  actionFuntion={() => {
                    validateWeekDaysShifts();
                  }}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }
);
